import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { DOCUMENT } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, NgZone, Output, TemplateRef } from "@angular/core";
import { Area, MissionSegmentStatus, PinData, RouteAreaTypeId, RouteData, mapSegmentStatusToClassConfig } from "@dtm-frontend/shared/ui";
import { ArrayUtils, DEFAULT_DEBOUNCE_TIME, FunctionUtils, LocalComponentStore, RxjsUtils, SpatialUtils } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AllGeoJSON, Polygon, feature as turfFeature } from "@turf/helpers";
import { ClassificationType, Color, CustomDataSource, PointGraphics, PolygonGraphics, PolylineGraphics, PositionProperty } from "cesium";
import { BehaviorSubject, combineLatestWith, distinctUntilChanged, map, of, shareReplay, switchMap, tap, throttleTime } from "rxjs";
import { MapLabelsUtils, MapUtils } from "../../index";
import { RouteEntity } from "../../models/route.models";
import { CameraHelperService } from "../../services/camera-helper.service";
import { CesiumMapService } from "../../services/cesium-map.service";
import { RouteViewerService } from "../../services/route-viewer/route-viewer.service";
import { CesiumImageObjectSettings, ROUTES_VISUAL_DATA, VisualDataOverride, VisualSettings } from "../route-viewer/route-viewer.data";

interface RouteViewLayerComponentState<T> {
    routeData: RouteData<T> | undefined;
    drawableFeatures: RouteAreaTypeId[];
    nearbyMissionsDrawableFeatures: RouteAreaTypeId[];
    activeEntitiesId: Set<string>;
    pinTemplate: TemplateRef<any> | null;
    isShown: boolean;
    shouldDisplayTimeRange: boolean;
}

const LABELS_SHOWING_DISTANCE_IN_METERS = 20000;

const FILL_IMAGE_SCALE = 15;

@UntilDestroy()
@Component({
    selector: "dtm-map-route-viewer-layer[routeData]",
    templateUrl: "./route-view-layer.component.html",
    styleUrls: ["./route-view-layer.component.scss", "../../../shared/styles/map-segment-pin.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class RouteViewLayerComponent<T> {
    @Input()
    public set routeData(value: RouteData<T> | undefined) {
        this.localStore.patchState({ routeData: value });
    }

    @Input()
    public set drawableFeatures(value: RouteAreaTypeId[] | undefined) {
        this.localStore.patchState({ drawableFeatures: [...new Set(value)] });
    }

    @Input()
    public set nearbyMissionsDrawableFeatures(value: RouteAreaTypeId[] | undefined) {
        this.localStore.patchState({ nearbyMissionsDrawableFeatures: value ? ArrayUtils.unique(value) : undefined });
    }

    @Input()
    public set pinTemplate(value: TemplateRef<any> | null | undefined) {
        this.localStore.patchState({ pinTemplate: value });
    }

    @Input() public set isShown(value: BooleanInput) {
        this.localStore.patchState({ isShown: coerceBooleanProperty(value) });
    }

    @Input() public set shouldDisplayTimeRange(value: BooleanInput) {
        this.localStore.patchState({ shouldDisplayTimeRange: coerceBooleanProperty(value) });
    }

    @Output()
    public readonly entityZoom = new EventEmitter<AllGeoJSON>();

    protected readonly groundWaypointsEntities$ = new BehaviorSubject<RouteEntity[]>([]);
    protected readonly pinEntities$ = this.initPinData();
    protected readonly blockEntities$ = this.initBlockPinData();

    protected readonly pinTemplate$ = this.localStore.selectByKey("pinTemplate");
    protected readonly isShown$ = this.localStore.selectByKey("isShown");
    protected readonly shouldDisplayTimeRange$ = this.localStore.selectByKey("shouldDisplayTimeRange");
    protected readonly areLabelsVisible$ = this.cameraHelperService.postRender$.pipe(
        map(() => this.cesiumService.getViewer().camera.positionCartographic.height <= LABELS_SHOWING_DISTANCE_IN_METERS),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    protected readonly isPathBased$ = this.localStore.selectByKey("routeData").pipe(map((routeData) => !!routeData?.isPathBased));
    protected readonly routeData$ = this.localStore.selectByKey("routeData");

    protected readonly MissionSegmentStatus = MissionSegmentStatus;

    private readonly cesiumViewer = this.cesiumService.getViewer();
    private readonly dataSource = new CustomDataSource();
    private readonly entityCollection = new Set<string>();

    constructor(
        private readonly localStore: LocalComponentStore<RouteViewLayerComponentState<T>>,
        private readonly routeViewerService: RouteViewerService<T>,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly cameraHelperService: CameraHelperService,
        private readonly cesiumService: CesiumMapService,
        private readonly zone: NgZone
    ) {
        this.localStore.setState({
            routeData: undefined,
            drawableFeatures: [],
            nearbyMissionsDrawableFeatures: [],
            activeEntitiesId: new Set(),
            pinTemplate: null,
            isShown: false,
            shouldDisplayTimeRange: true,
        });

        this.watchLabelsOverlapping();
        this.cesiumViewer.dataSources.add(this.dataSource);
        this.initFlightAreaData();
    }

    protected getPinClassConfig(pinData: PinData<T>) {
        return {
            ...mapSegmentStatusToClassConfig(pinData.status),
            main: pinData.isMain,
            selected: pinData.isSelected,
            collision: pinData.isCollision,
        };
    }

    private initFlightAreaData() {
        this.localStore
            .selectByKey("routeData")
            .pipe(combineLatestWith(this.localStore.selectByKey("isShown")), untilDestroyed(this))
            .subscribe(([routeData, isShown]) => {
                const currentIds = new Set<string>();
                if (!routeData || !isShown) {
                    this.dataSource.entities.removeAll();
                    this.entityCollection.clear();

                    return;
                }

                const routes = [routeData, ...(routeData.nearbyMissionsData ?? [])];
                const routesEntities = routes.flatMap((route) => this.routeViewerService.transformRouteDataToFlightAreaEntities(route));

                routesEntities.forEach((entity, index) => {
                    const id = entity.id + index;
                    currentIds.add(id);
                    const area = this.dataSource.entities.getOrCreateEntity(id);
                    area.polyline = {
                        positions: entity.positions,
                        material: this.getRouteEntityMaterial(entity),
                        width: this.getRouteEntityOutlineWidth(entity),
                        clampToGround: true,
                    } as unknown as PolylineGraphics;
                    area.polygon = {
                        hierarchy: entity.positions,
                        material: this.getRouteEntityFill(entity),
                        classificationType: ClassificationType.BOTH,
                    } as unknown as PolygonGraphics;
                });

                const pathEntities = routes.flatMap((route) => this.routeViewerService.transformRouteDataToPathEntities(route));

                pathEntities.forEach((entity, index) => {
                    const id = entity.id + index;
                    currentIds.add(id);
                    const path = this.dataSource.entities.getOrCreateEntity(id);
                    path.polyline = {
                        positions: entity.positions,
                        material: this.getRouteEntityMaterial(entity),
                        width: this.getRouteEntityOutlineWidth(entity),
                        clampToGround: true,
                    } as unknown as PolylineGraphics;
                });

                const waypointEntities = routes.flatMap((route) => this.routeViewerService.transformRouteDataToWaypointEntities(route));
                this.groundWaypointsEntities$.next(waypointEntities.filter(({ data }) => data?.isLanding || data?.isTakeoff));

                waypointEntities.forEach((entity, index) => {
                    const id = entity.id + index;
                    currentIds.add(id);
                    const waypoint = this.dataSource.entities.getOrCreateEntity(id);
                    waypoint.position = entity.positions[0] as unknown as PositionProperty;
                    waypoint.point = {
                        pixelSize: 8,
                        color: this.getRouteEntityFill(entity),
                        show: true,
                    } as unknown as PointGraphics;
                });

                currentIds.forEach((id) => this.entityCollection.add(id));
                this.entityCollection.forEach((id) => {
                    if (!currentIds.has(id)) {
                        this.dataSource.entities.removeById(id);
                        this.entityCollection.delete(id);
                    }
                });

                this.cesiumViewer.scene.requestRender();
            });
    }

    private initPinData() {
        return this.localStore.selectByKey("routeData").pipe(
            RxjsUtils.filterFalsy(),
            combineLatestWith(
                this.localStore.selectByKey("drawableFeatures"),
                this.localStore.selectByKey("nearbyMissionsDrawableFeatures")
            ),
            map(([routeData, drawableFeatures, nearbyMissionsDrawableFeatures]) =>
                this.combineRoutesPinData(routeData, drawableFeatures, nearbyMissionsDrawableFeatures)
            )
        );
    }

    private combineRoutesPinData(
        routeData: RouteData<T>,
        drawableFeatures: RouteAreaTypeId[],
        nearbyMissionsDrawableFeatures: RouteAreaTypeId[]
    ) {
        const mainRoutePins = this.routeViewerService.mapRouteToPinEntities(routeData, drawableFeatures);
        const nearbyMissionsPins = (routeData.nearbyMissionsData ?? []).flatMap((nearbyMission) =>
            this.routeViewerService.mapRouteToPinEntities(nearbyMission, nearbyMissionsDrawableFeatures)
        );

        return [...mainRoutePins, ...nearbyMissionsPins];
    }

    private initBlockPinData() {
        return this.localStore.selectByKey("routeData").pipe(
            RxjsUtils.filterFalsy(),
            combineLatestWith(this.localStore.selectByKey("drawableFeatures")),
            map(([routeData, drawableFeatures]) => this.routeViewerService.transformRouteDataToBlockPinEntity(routeData, drawableFeatures))
        );
    }

    protected getRouteEntityFill(entity: RouteEntity) {
        const visualData = this.getEntityVisualData(entity);

        if (visualData?.fill instanceof CesiumImageObjectSettings && entity.degreesPositions) {
            const geometry: Polygon = {
                type: "Polygon",
                coordinates: [entity.degreesPositions],
            };

            return visualData.fill.createImageMaterialProperty(MapUtils.getCesiumImageScaleForGeometry(geometry, FILL_IMAGE_SCALE));
        }

        return visualData?.fill ?? Color.TRANSPARENT;
    }

    protected getRouteEntityOutlineWidth(entity: RouteEntity) {
        return this.getEntityVisualData(entity)?.outlineWidth ?? 0;
    }

    protected getRouteEntityMaterial(entity: RouteEntity) {
        return this.getEntityVisualData(entity)?.outlineMaterial;
    }

    protected getHeightRangeValues(flightArea?: Area) {
        if (!flightArea) {
            return;
        }

        const elevationMin = flightArea.elevationMin ?? 0;
        const elevationMax = flightArea.elevationMax ?? 0;
        const isOnTheGround = flightArea.volume.floor <= elevationMin;
        const minHeightAmsl = flightArea.volume.floor;
        const maxHeightAmsl = flightArea.volume.ceiling;
        const maxHeightAgl = flightArea.volume.ceiling - elevationMax;
        const minHeightAgl = flightArea.volume.floor - elevationMin;

        return {
            minHeightAmsl,
            maxHeightAmsl,
            isOnTheGround,
            maxHeightAgl,
            minHeightAgl,
        };
    }

    protected getZoomArea(routeData?: RouteData<T>) {
        if (!routeData?.route.sections[0].flightZone?.flightArea.volume.area) {
            return;
        }

        const features = routeData?.route.sections
            .map((section) => {
                const area = section.flightZone?.flightArea.volume.area ?? section.segment?.flightArea.volume.area;
                if (!area) {
                    return;
                }

                return turfFeature(area);
            })
            .filter(FunctionUtils.isTruthy);

        return SpatialUtils.union(features);
    }

    private getEntityVisualData(entity: RouteEntity): VisualSettings | undefined {
        let visualData = ROUTES_VISUAL_DATA[entity.id]?.[MissionSegmentStatus.DEFAULT];

        if (!entity.isMain) {
            visualData = { ...visualData, ...this.findVisualDataOverride(entity, "nearbyMissionOverride") };
        }

        if (entity.isCollision) {
            visualData = { ...visualData, ...this.findVisualDataOverride(entity, "collisionMissionOverride") };
        }

        if (entity.isSelected) {
            visualData = { ...visualData, ...this.findVisualDataOverride(entity, "selectedNearbyMissionOverride") };
        }

        if (entity.isMain) {
            visualData = { ...visualData, ...this.findVisualDataOverride(entity) };
        }

        if (entity.isEmergency) {
            visualData = { ...visualData, ...this.findVisualDataOverride(entity, "emergencyOverride") };
        }

        return visualData;
    }

    private findVisualDataOverride(entity: RouteEntity, overrideKey?: keyof VisualDataOverride) {
        const segmentStatus = entity.segmentStatus ?? MissionSegmentStatus.DEFAULT;

        if (!overrideKey) {
            return ROUTES_VISUAL_DATA[entity.id]?.[segmentStatus] ?? ROUTES_VISUAL_DATA[entity.id]?.[MissionSegmentStatus.DEFAULT];
        }

        return (
            ROUTES_VISUAL_DATA[entity.id]?.[segmentStatus]?.[overrideKey] ??
            ROUTES_VISUAL_DATA[entity.id]?.[MissionSegmentStatus.DEFAULT]?.[overrideKey]
        );
    }

    private watchLabelsOverlapping() {
        this.isShown$
            .pipe(
                switchMap((isShown) => {
                    if (!isShown) {
                        return of(undefined);
                    }

                    return this.cameraHelperService.postRender$.pipe(
                        throttleTime(DEFAULT_DEBOUNCE_TIME, undefined, { leading: true, trailing: true }),
                        tap(() => this.zone.runOutsideAngular(() => this.removeOverlapping()))
                    );
                }),
                untilDestroyed(this)
            )
            .subscribe();
    }

    private removeOverlapping() {
        const elements = [
            ...(this.document?.querySelectorAll(".segment-pin:not(.simple), .zone-alert").values() ?? []),
        ] as HTMLDivElement[];

        MapLabelsUtils.removeOverlapping(elements, {
            svgLineSelector: ".line-connector line",
            buffer: 4,
        });
    }
}
